import * as React from 'react'

import isEmpty from 'lodash/isEmpty'
import { Moment } from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Text from 'modules/core/components/Inputs/Text'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import documents from 'modules/documents/store/collections/documents'
import DocumentUploaderWithLinks from 'modules/timeoff/components/DocumentUploaderWithLinks'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import type Leave from 'modules/timeoff/store/models/Leave'

const getTypesLabel = (type: string) => {
  switch (type) {
    case 'accident_other':
      return i18n.t('time_off.medical_leave.type.types.accident_other')
    case 'accident_working':
      return i18n.t('time_off.medical_leave.type.types.accident_working')
    case 'lactation':
      return i18n.t('time_off.medical_leave.type.types.lactation')
    case 'maternity':
      return i18n.t('time_off.medical_leave.type.types.maternity')
    case 'paternity':
      return i18n.t('time_off.medical_leave.type.types.paternity')
    case 'sickness_common':
      return i18n.t('time_off.medical_leave.type.types.sickness_common')
    case 'sickness_professional':
      return i18n.t('time_off.medical_leave.type.types.sickness_professional')
    default:
      throw new Error(`Unknown translation for type: ${type}`)
  }
}

interface Props {
  form: LeaveForm
  leave: Leave
  readOnly: boolean
  contractStartsOn: Moment | null | undefined
}

const Content: React.FC<Props> = (props) => {
  const { form, leave, readOnly, contractStartsOn } = props

  const documentRecord = React.useMemo(() => {
    if (!leave) return null
    return documents.get(leave.attributes.medical_leave_document?.id)
  }, [leave])

  const onDeleteDocument = React.useCallback(() => {
    if (!leave) return
    leave.set({ document: {} })
  }, [leave])

  const canManageLeaveDocuments = React.useMemo(
    () => leave.policy.can('manageLeaveDocuments'),
    [leave]
  )

  const minDate = contractStartsOn

  const renderMedicalLeaveType = React.useCallback(() => {
    const label = i18n.t('time_off.medical_leave.type.label')
    const field = form.get('medical_leave_type')
    const value = isEmpty(field.value) ? '-' : getTypesLabel(field.value ?? '')
    return <TextField readonly label={label} value={value} />
  }, [form])

  const renderStartDate = React.useCallback(() => {
    return (
      <FormRow>
        <DayPicker
          field={form.get('start_on')}
          label={i18n.t('common.starting_from')}
          minDate={minDate}
          readonly={readOnly}
        />
      </FormRow>
    )
  }, [form, minDate, readOnly])

  const renderColegiateField = React.useCallback(() => {
    const accident = form.isAccident
    const sickness = form.isSickness
    if (!(accident || sickness)) return null
    return (
      <FormRow>
        <Text
          field={form.get('colegiate_number')}
          label={i18n.t('time_off.medical_leave.colegiate_number.label')}
          secondaryLabel={
            <InformationTooltip
              information={i18n.t('time_off.medical_leave.colegiate_number.information')}
              icon="question"
            />
          }
          readonly={readOnly}
        />
      </FormRow>
    )
  }, [form, readOnly])

  return (
    <>
      <FormRow>{renderMedicalLeaveType()}</FormRow>
      {renderStartDate()}
      <FormRow>{renderColegiateField()}</FormRow>
      <FormRow>
        <DocumentUploaderWithLinks
          document={documentRecord}
          field={form.get('medical_leave_document')}
          onDeleteDocument={onDeleteDocument}
          placeholder={i18n.t('time_off.leave_document')}
          readOnly={readOnly || !canManageLeaveDocuments}
        />
      </FormRow>
    </>
  )
}

export default observer(Content)
